import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import { format } from "../../intl.js";
import { fetchPaymentHistory } from "../../api/index.js";

// Isolated tab: fetches from the new /payment-history/client endpoint only.
// Does not touch Statement's data, props, or running-balance logic.

const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginTop: 20,
    display: "flex",
    flexDirection: "column",
    // Keeps the page height stable: the table body scrolls internally
    // instead of the page growing with the row count.
    maxHeight: "calc(100vh - 320px)",
    minHeight: 300,
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 16,
    marginBottom: 16,
  },
  filterControl: {
    minWidth: 180,
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: 200,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  table: {
    tableLayout: "fixed",
  },
  headerCell: {
    fontWeight: 600,
    fontSize: 12.5,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    padding: "12px 16px",
    verticalAlign: "middle",
    textAlign: "center",
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  bodyRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  cell: {
    padding: "12px 16px",
    verticalAlign: "middle",
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  statusCell: {
    padding: "12px 16px",
    verticalAlign: "middle",
    textAlign: "center",
    whiteSpace: "nowrap",
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  remarksCell: {
    padding: "12px 16px",
    verticalAlign: "middle",
    textAlign: "center",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  colSno: { width: "5%" },
  colDate: { width: "10%" },
  colType: { width: "11%" },
  colTxnAmount: { width: "12%" },
  colOutstanding: { width: "12%" },
  colStatus: { width: "35%" },
  // Last column: no trailing right border against the table edge.
  colRemarks: { width: "15%", borderRight: "none" },
  chip: {
    borderRadius: 16,
    minWidth: 84,
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 12,
    height: 26,
  },
  invoiceChip: {
    backgroundColor: "#eceff1",
    color: "#37474f",
    border: "1px solid #cfd8dc",
  },
  paymentChip: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    border: "1px solid #ffe0b2",
  },
  receiptChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    border: "1px solid #c8e6c9",
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
    color: "gray",
  },
}));

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100, { label: "All", value: -1 }];

const PaymentHistory = ({ client }) => {
  const classes = useStyles();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [transactionTypeFilter, setTransactionTypeFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const clientId = client?._id;

  useEffect(() => {
    if (!clientId) return;

    let isMounted = true;
    setIsLoading(true);
    setError("");

    fetchPaymentHistory(clientId)
      .then((res) => {
        if (!isMounted) return;
        setHistory(res.data?.history || []);
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Unable to load payment history.");
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [clientId]);

  const filteredHistory = useMemo(() => {
    if (transactionTypeFilter === "All") return history;
    return history.filter((row) => row.transactionType === transactionTypeFilter);
  }, [history, transactionTypeFilter]);

  useEffect(() => {
    setPage(0);
  }, [transactionTypeFilter, rowsPerPage]);

  const pagedHistory = useMemo(() => {
    if (rowsPerPage === -1) return filteredHistory;
    const start = page * rowsPerPage;
    return filteredHistory.slice(start, start + rowsPerPage);
  }, [filteredHistory, page, rowsPerPage]);

  // Business rules (source of truth):
  //   Receipt -> ASN pays money to the Agent
  //   Payment -> ASN receives money from the Agent
  //   Invoice  -> signed total applied to running outstanding
  //              (+ Agent owes ASN, - ASN owes Agent)
  // Wording only - the running Outstanding Amount itself is computed
  // upstream and is not touched here.
  const buildStatusDetails = (row) => {
    if (row.transactionType === "Invoice") {
      const invoiceLabel = `Invoice${row.invoiceNumber ? ` #${row.invoiceNumber}` : ""} created`;
      const outstanding = Number(row.outstandingAmount || 0);
      if (outstanding < 0) {
        return `${invoiceLabel}. Agent has to pay ASN ${format(Math.abs(outstanding))}.`;
      }
      if (outstanding > 0) {
        return `${invoiceLabel}. ASN has to pay the Agent ${format(outstanding)}.`;
      }
      return `${invoiceLabel}. No outstanding balance.`;
    }
    if (row.transactionType === "Receipt") {
      return `ASN paid ${format(row.transactionAmount)} to the Agent. Outstanding Amount: ${format(
        row.outstandingAmount
      )}`;
    }
    if (row.transactionType === "Payment") {
      return `ASN received ${format(row.transactionAmount)} from the Agent. Outstanding Amount: ${format(
        row.outstandingAmount
      )}`;
    }
    return "";
  };

  const getChipClass = (transactionType) => {
    if (transactionType === "Invoice") return classes.invoiceChip;
    if (transactionType === "Payment") return classes.paymentChip;
    return classes.receiptChip;
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => setRowsPerPage(Number(event.target.value));

  return (
    <div className={classes.wrapper}>
      <div className={classes.filterBar}>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel id="transaction-type-filter-label">Transaction Type</InputLabel>
          <Select
            labelId="transaction-type-filter-label"
            label="Transaction Type"
            value={transactionTypeFilter}
            onChange={(e) => setTransactionTypeFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Invoice">Invoice</MenuItem>
            <MenuItem value="Payment">Payment</MenuItem>
            <MenuItem value="Receipt">Receipt</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="textSecondary">
          {filteredHistory.length} record{filteredHistory.length === 1 ? "" : "s"}
        </Typography>
      </div>

      {isLoading && (
        <div className={classes.emptyState}>
          <CircularProgress size={28} />
        </div>
      )}

      {!isLoading && error && <div className={classes.emptyState}>{error}</div>}

      {!isLoading && !error && filteredHistory.length === 0 && (
        <div className={classes.emptyState}>
          No payment/receipt history for the selected Hotel/Agent.
        </div>
      )}

      {!isLoading && !error && filteredHistory.length > 0 && (
        <>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={`${classes.headerCell} ${classes.colSno}`}>S.No</TableCell>
                  <TableCell className={`${classes.headerCell} ${classes.colDate}`}>Date</TableCell>
                  <TableCell className={`${classes.headerCell} ${classes.colType}`}>
                    Transaction Type
                  </TableCell>
                  <TableCell className={`${classes.headerCell} ${classes.colTxnAmount}`}>
                    Transaction Amount
                  </TableCell>
                  <TableCell className={`${classes.headerCell} ${classes.colOutstanding}`}>
                    Outstanding Amount
                  </TableCell>
                  <TableCell className={`${classes.headerCell} ${classes.colStatus}`}>
                    Status Details
                  </TableCell>
                  <TableCell className={`${classes.headerCell} ${classes.colRemarks}`}>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedHistory.map((row) => (
                  <TableRow key={row.id || row.sNo} className={classes.bodyRow}>
                    <TableCell className={`${classes.cell} ${classes.colSno}`}>{row.sNo}</TableCell>
                    <TableCell className={`${classes.cell} ${classes.colDate}`}>
                      {row.date ? moment(row.date).format("DD-MMM-YYYY") : "-"}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.colType}`}>
                      <Chip
                        size="small"
                        label={row.transactionType}
                        className={`${classes.chip} ${getChipClass(row.transactionType)}`}
                      />
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.colTxnAmount}`}>
                      {row.transactionAmount != null ? format(row.transactionAmount) : "-"}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.colOutstanding}`}>
                      {format(row.outstandingAmount)}
                    </TableCell>
                    <TableCell className={`${classes.statusCell} ${classes.colStatus}`}>
                      {buildStatusDetails(row)}
                    </TableCell>
                    <TableCell className={`${classes.remarksCell} ${classes.colRemarks}`}>
                      {row.remarks || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            count={filteredHistory.length}
            rowsPerPage={rowsPerPage === -1 ? filteredHistory.length : rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </div>
  );
};

export default PaymentHistory;
